/**
 * App-lock helpers — a biometric/passcode gate for the installed PWA.
 *
 * Threat: a home-screen PWA keeps the Firebase session, so an unlocked/borrowed
 * phone could open the app and reach HR data. This locks the app on cold launch
 * and after 5 minutes of inactivity, requiring a WebAuthn platform-authenticator
 * gesture (Face ID / Touch ID / fingerprint / device passcode) to continue.
 *
 * This is a CLIENT-SIDE gate (a strong deterrent, not tamper-proof — the token
 * still lives in device storage; see the security risk assessment R11). It only
 * engages when running as an installed PWA AND the device actually has a
 * user-verifying platform authenticator (otherwise we'd degrade to constant
 * sign-outs, which is worse than the risk).
 */

export const IDLE_MS = 5 * 60 * 1000; // 5 minutes
const CRED_KEY = 'daaily.applock.credId';
export const LAST_ACTIVE_KEY = 'daaily.applock.lastActive';

/** Running as an installed PWA (home-screen app), not a normal browser tab. */
export function isStandalone(): boolean {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      // iOS Safari home-screen apps
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** Does the device have a usable biometric/passcode authenticator? */
export async function platformAuthAvailable(): Promise<boolean> {
  try {
    if (typeof window.PublicKeyCredential === 'undefined') return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export function hasRegisteredCredential(): boolean {
  return !!localStorage.getItem(CRED_KEY);
}

// base64url <-> ArrayBuffer
const bufToB64u = (b: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
const b64uToBuf = (s: string): Uint8Array =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

/** One-time: register a platform credential for this device. Triggers the
 *  biometric enrolment prompt. Returns true on success. */
export async function registerAppLock(email: string): Promise<boolean> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = new TextEncoder().encode(email).slice(0, 64);
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: 'DAAily Hub' }, // id defaults to the current origin's domain
      user: { id: userId, name: email, displayName: email },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60_000,
      attestation: 'none',
    },
  })) as PublicKeyCredential | null;
  if (!cred) return false;
  localStorage.setItem(CRED_KEY, bufToB64u(cred.rawId));
  return true;
}

/** Prompt for the biometric/passcode and return true if verified. Local-only
 *  (no server verification) — the value is the forced user-verification gesture. */
export async function verifyAppLock(): Promise<boolean> {
  const stored = localStorage.getItem(CRED_KEY);
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge,
    userVerification: 'required',
    timeout: 60_000,
  };
  if (stored) {
    publicKey.allowCredentials = [{ id: b64uToBuf(stored) as BufferSource, type: 'public-key' }];
  }
  const assertion = await navigator.credentials.get({ publicKey });
  return !!assertion;
}

export function readLastActive(): number {
  const v = Number(localStorage.getItem(LAST_ACTIVE_KEY));
  return Number.isFinite(v) ? v : 0;
}
export function writeLastActive(ts: number): void {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(ts));
  } catch {
    /* ignore quota/private-mode errors */
  }
}
